import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Activity, ActivityType } from '@omni/classes/activity/activity.class';
import { AllocationOrderType } from '@omni/enums/allocation/allocation.enum';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { EmailViewType, EmailActivity } from '@omni/classes/activity/email.activity.class';
import { FOLLOW_UP_TYPE, FollowUpActivity } from '@omni/classes/activity/follow-up-action.activity.class';
import { OrderActivity } from '@omni/classes/activity/order.activity.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { CreateSampleDropRequestBody, SampleActivity } from '@omni/classes/activity/sample.activity.class';
import { SetBookingActivity, SetBookingStatus } from '@omni/classes/activity/set-booking.activity.class';
import { MedicalInquiry } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { TimeOffActivity } from '@omni/classes/activity/timeoff.class';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { MeetingStatus } from '@omni/classes/meeting/meeting.class';
import { GoAppActivitiesComponent } from '@omni/components/go-app-activities/go-app-activities';
import { IndFilterMenuModalComponent, IndFilterMenuModalDataModel } from '@omni/components/shared/ind-filter-menu-modal/ind-filter-menu-modal';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { ActivityDataService } from '@omni/data-services/activity/activity.service';
import { FollowUpActivityDataService, OperationDetail } from '@omni/data-services/follow-up-activity/follow-up-activity.data.service';
import { MeetingDataService } from '@omni/data-services/meeting/meeting.data.service';
import { OrderActivityDataService } from '@omni/data-services/order-activity/order-activity.data.service';
import { PhoneCallDataService, PhonecallStatus } from '@omni/data-services/phone-call/phonecall.data.service';
import { RepServices } from '@omni/data-services/rep/rep.services';
import { SampleDataService } from '@omni/data-services/sample/sample.data.service';
import { SdrStatus } from '@omni/classes/sample/sample-delivery-request.class';
import { SampleDeliveryRequestService } from '@omni/services/sample/sample-delivery-request.service';
import { MeetingExpenseService } from '@omni/services/meeting-expense/meeting-expense.service';
import { SetBookingDataService } from '@omni/data-services/set-booking/set-booking.data.service';
import { SurgeryOrderActivityDataService } from '@omni/data-services/surgery-order-activity/surgery-order-activity.data.service';
import { WebsocketDataService } from '@omni/data-services/websocket/websocket.data.service';
import { REP_STATUS } from '@omni/models/rep-status-model';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ActivityScrapStatus, ActivityService } from '@omni/services/activity/activity.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { AccesingMode, CaseManagementService } from '@omni/services/case-management/case-management.service';
import { CoachingReportService } from '@omni/services/coaching/coaching.report.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { DeviceService } from '@omni/services/device/device.service';
import { EmailService } from '@omni/services/email-templates/email.service';
import { EventsService } from '@omni/services/events/events.service';
import { FooterService, FooterViews } from '@omni/services/footer/footer.service';
import { PhoneCallMeetingStructureService } from '@omni/services/phone-call-meeting-structure/phone-call-meeting-structure.service';
import { TrackingEventNames, TrackService } from '@omni/services/logging/tracking.service';
import { ChildNavNames, NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { SampleService, SamplingDetailsViewMode } from '@omni/services/sample/sample.service';
import { StoreCheckService } from '@omni/services/store-check/store-check.service';
import { TimeOffUtilityService } from '@omni/services/time-off/time-off-utility.service';
import { TimeOffService } from '@omni/services/time-off/time-off.service';
import { UIService } from '@omni/services/ui/ui.service';
import { MedicalInquiryService } from '@omni/services/medical-inquiry/medical-inquiry.service';
import _ from 'lodash';
import { MeetingStructureService } from '@omni/services/meeting-structure/meeting-structure.service';
import { FeatureProfiler } from '@omni/services/logging/feature-profiler.service';
import { takeUntil, Subject } from 'rxjs';

@Component({
  selector: 'go-app-activities-page',
  templateUrl: './go-app-activities-page.component.html',
  styleUrl: './go-app-activities-page.component.scss',
  // OnPush deferred — child go-app-todo loads data async via events/services without
  // observable subscriptions. OnPush on this page blocks CD for todo's async data loads.
})
export class GoAppActivitiesPageComponent implements OnInit, OnDestroy {

  @ViewChild('activitiespagerightpane', { static: true }) public navCtrl: IonNav;
  @ViewChild(GoAppActivitiesComponent) goAppActivitiesComponent: GoAppActivitiesComponent;
  @ViewChild('customersPane') public customersPane: ElementRef;
  @Input() public __source = '';
  @Input() public retainSelectedActivity = false;
  public isMobileView: boolean = false;
  public isMobilePotrait: boolean = false;
  public selectedView: 'dashboard' | 'calendar' | 'more' = 'dashboard';
  public showMetrics: boolean = false;
  public activityOpenedFrom: 'todo' | 'activities' = 'activities';

  /** Local reactive copies of UIService state for OnPush change detection */
  uiShowRightPane: boolean = false;
  showRightPaneOnly: boolean = false;

  private _newActivityOpened: boolean;

  set newActivityOpened(value: boolean) {
    this._newActivityOpened = value;
    //In mobile view, display list view by default, on opening new activity page
    if (value && this.device.isMobileDevice && this.selectedView != 'calendar') {
      this.selectedView = 'calendar';
      this.events.publish('footer:selectedViewChanged', this.selectedView);
    }
    if (this._newActivityOpened) {
      this.activityOpenedFrom = 'activities';
      //Clear if any activity is opened
      if (this.device.isMobileDevice) {
        this.uiService.showRightPane = false;
        this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
      }
      this.activityService.selectedActivity = null;
    }
  }

  get newActivityOpened(): boolean {
    return this._newActivityOpened;
  }
  private readonly destroy$ = new Subject<boolean>();
  private _isAgendaRefreshed: boolean = false;

  constructor(
    public uiService: UIService,
    private navService: NavigationService,
    public device: DeviceService,
    private authService: AuthenticationService,
    private events: EventsService,
    private activityService: ActivityService,
    public trackingService: TrackService,
    private timeOffUtilityService: TimeOffUtilityService,
    private timeOffService: TimeOffService,
    private websocket: WebsocketDataService,
    private meetingService: MeetingDataService,
    private alertService: AlertService,
    private sampleDataService: SampleDataService,
    private sampleDeliveryRequestService: SampleDeliveryRequestService,
    private meetingExpenseService: MeetingExpenseService,
    private footerService: FooterService,
    private phonecallDataService: PhoneCallDataService,
    private translate: TranslateService,
    private callplanService: CallPlanOfflineService,
    private notificationService: NotificationService,
    private storeCheckService: StoreCheckService,
    private emailService: EmailService,
    private orderActivityDataService: OrderActivityDataService,
    private surgeryOrderService: SurgeryOrderActivityDataService,
    private activityDataService: ActivityDataService,
    private followUpDataService: FollowUpActivityDataService,
    private caseManagementService: CaseManagementService,
    private setBookingDataService: SetBookingDataService,
    private readonly _cd: ChangeDetectorRef,
    private readonly presentationService: PresentationService,
    private readonly accountOfflineService: AccountOfflineService,
    private readonly sampleService: SampleService,
    private readonly coachingReportService: CoachingReportService,
    private readonly _ngZone: NgZone,
    private readonly contactService: ContactOfflineService,
    private repService: RepServices,
    private phoneCallMeetingStructureService: PhoneCallMeetingStructureService,
    private medicalInquiryService: MedicalInquiryService,
    private readonly featureProfiler: FeatureProfiler
  ) {

    this.events.observe('sync:completed').subscribe(() => {
      this.showMetrics = this.hasMetricsFeaturesMapped();
      if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
        this.uiService.showRightPane = false;
        this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
        this.activityService.selectedActivity = this.activityService.selectedActivityAtHome = null;
      }
      this._cd.markForCheck();
    });
    this.showMetrics = this.hasMetricsFeaturesMapped();

    this.events.unsubscribe('deleteMeeting')
    this.events.subscribe('deleteMeeting', (activity: Activity, forcePopup) => {
      this.deleteActivity(activity ? activity : this.activityService.selectedActivity, false, forcePopup);
    });

    this.events.observe('footer:selectedViewChanged')
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedView: 'dashboard' | 'calendar' | 'more') => {
        if (selectedView === 'dashboard') {
          this.clearFilter();
        }
        this.selectedView = selectedView;
        this._cd.markForCheck();
      });

    this.events.subscribe('deleteMeetingNoConfirm', () => {
      this.deleteActivity(this.activityService.selectedActivity, true);
    });

    this.events.observe('newActivityOpenedChanged').pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.newActivityOpened = value;
      this._cd.markForCheck();
    });

    this.events.observe('scrollToDate')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.activityOpenedFrom === 'todo') {
          this.activityOpenedFrom = 'activities';
          this.events.publish('initTodo');
          this._cd.markForCheck();
        }
      });

  }

  private readonly profiler = this.featureProfiler.for('activities');

  ngOnInit() {
    this.profiler.startSession();
    if (!this.retainSelectedActivity) {
      this.activityService.selectedActivity = this.activityService.selectedActivityAtHome = null;
    } else {
      this.events.publish('setSelectedNavView', 'dashboard');
    }

    this.uiService.showRightPane$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.uiShowRightPane = value;
      this._cd.markForCheck();
    });

    this.uiService.showRightPaneOnly$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.showRightPaneOnly = value;
      this._cd.markForCheck();
    });
  }

  ionViewWillEnter() {
    this.uiService.toolsActivityActive = false;
    if (!this.device.isOffline && this.repService.getCurrentUserState() === REP_STATUS.IN_MEETING.userState) {
        let newStateData = { userState: REP_STATUS.ONLINE.userState };
        this.repService.setCurrentState(newStateData);
    }
    if(this.uiService.agendaRefreshRequired){
      this.events.publish('refreshAgenda');
      this.uiService.agendaRefreshRequired = false;
    }
  }

  clearFilter() {
    this.authService.impersonatedUser = undefined;
    this.activityService.agendaTeamViewActivities = [];
    this.activityService.multiOptionActivityFilter = [];
    this.activityService.activityFilter = ActivityType.AllActivity;
    this.goAppActivitiesComponent?.clearSearchBar();
  }

  deleteActivity(activity, force?: boolean, forcePopup?: boolean) {
    if (!activity || activity.isCompleted) return;
    const accountVisitRecordCheckResponse = this.activityService.accountVisitRecordCheck(activity);
    // Prevent account visit offline request
    if (this.activityService.accountVisitOfflineCheck(
      accountVisitRecordCheckResponse,
      true,
    )) {
      return;
    }

    const scrapStatusResponse = this.activityService.getActivityScrapStatus(activity);
    let scrapStatus: ActivityScrapStatus = scrapStatusResponse[activity.ID] ?? undefined;
    let shouldHaltScrap = scrapStatus
      ? this.activityService.checkAndNotifyScrapProcessHalt(scrapStatus)
      : false;
    const accountVisitNestedMeetings = accountVisitRecordCheckResponse.isAccountVisitRecord
      ? this.activityService.getAccountVisitNestedMeetings(activity.ID)
      : [];

    if (
      !shouldHaltScrap
      && activity instanceof AppointmentActivity
      && activity.indskr_isparentcall
      && Array.isArray(accountVisitNestedMeetings)
      && accountVisitNestedMeetings.length > 0
    ) {
      // Check for account visit nested meetings
      for (let i = 0; i < accountVisitNestedMeetings.length; i++) {
        const nestedMeeting = accountVisitNestedMeetings[i];
        const nestedMeetingScrapStatus = scrapStatusResponse[nestedMeeting.ID] ?? undefined;
        shouldHaltScrap = !nestedMeetingScrapStatus
          ? shouldHaltScrap
          : this.activityService.checkAndNotifyScrapProcessHalt(nestedMeetingScrapStatus);

        if (shouldHaltScrap) {
          break;
        }
      }
    }

    // Check for submitted Sample Delivery Requests raised from this meeting
    // (and, for account visits, its nested meetings) — same block as a
    // completed Sample Drop / Allocation Order (OMNI-61272).
    if (!shouldHaltScrap && activity instanceof AppointmentActivity) {
      if (this.sampleDeliveryRequestService.hasSdrForMeeting(activity.ID)) {
        shouldHaltScrap = this.activityService.checkAndNotifyScrapProcessHalt(ActivityScrapStatus.HASSUBMITTEDSDR);
      } else if (Array.isArray(accountVisitNestedMeetings) && accountVisitNestedMeetings.length > 0) {
        for (let i = 0; i < accountVisitNestedMeetings.length; i++) {
          if (this.sampleDeliveryRequestService.hasSdrForMeeting(accountVisitNestedMeetings[i].ID)) {
            shouldHaltScrap = this.activityService.checkAndNotifyScrapProcessHalt(ActivityScrapStatus.HASSUBMITTEDSDR);
            break;
          }
        }
      }
    }

    if (!shouldHaltScrap && activity instanceof AppointmentActivity) {
      const meetingIds = [activity.ID, ...(Array.isArray(accountVisitNestedMeetings) ? accountVisitNestedMeetings.map(m => m.ID) : [])];
      if (meetingIds.some(id => this.meetingExpenseService.hasNonDraftExpenses(id))) {
        shouldHaltScrap = this.activityService.checkAndNotifyScrapProcessHalt(ActivityScrapStatus.HASNONDRAFTEXPENSE);
      }
    }

    // Not scrap-able
    if (shouldHaltScrap) {
      return;
    }

    if (activity.type == ActivityType.FollowUp && activity.ownerId != this.authService.user.systemUserID) {
      return;
    }
    if (activity.type === ActivityType.MedicalInquiry) {
      const mi = activity as MedicalInquiry;
      if (!mi.isDraft || !mi.isActive || mi.ownerId !== this.authService.user.systemUserID) {
        return;
      }
    }
    //CWD-3077
    if (activity.type == 'TimeOffRequest') {
      this.trackingService.tracking('TimeOffScrapAgenda', TrackingEventNames.TIMEOFF);
      this.timeOffService.setSelectedTot(activity);
      this.timeOffUtilityService.scrapTimeOff(activity, "agenda");
    }
    else {
      if (force) {
        if (activity.type == ActivityType.PhoneCall) {
          this.deletePhoneCall(activity, true);
          return
        }
        if (this.activityService.selectedActivity) {
          this.websocket.sendRemoteMeetingEnd(this.activityService.selectedActivity, true);
          this.websocket.removeSubscription(this.activityService.selectedActivity);
          this.websocket.meetingInProgress = false;
        }
        this.meetingService.updateMeetingStatus(activity, MeetingStatus.CANCELED)
          .then(() => {
            if (this.navService.getCurrentPageName() === PageName.ActivitiesDetailsPaneComponent && this.device.isMobileDevice) {
              this.navService.popWithPageTracking();
            } else if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
              this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
              if (this.footerService.isActivitiesButton && this.uiService.showRightPane) this.uiService.showRightPane = false;
              else this.events.publish("go-back-to-xperiences", PageName.GoAppActivitiesPageComponent);
            } else if (this.navService.getCurrentMasterPageName() == PageName.OpportunityManagementPage
              || ((this.navService.getCurrentPageName() === PageName.ContactPageComponent || this.navService.getCurrentPageName() === PageName.AccountPageComponent)
                && (this.navService.getPreviousActiveChildNavViewPageName() != undefined))) {
              this.navService.popChildNavPageWithPageTracking();
            } else if (this.uiService.prevView == 'callPlanDetails' || this.navService.getCurrentMasterPageName() == PageName.CallPlanComponent || this.navService.getCurrentMasterPageName() == PageName.CustomerCallPlanPage
              || (this.navService.getCurrentMasterPageName() == PageName.GoAppActivitiesPageComponent && this.uiService.activitiesPageTab == 'plans' && this.uiService.activitiesPagePlanTabSelectedPlan == 'goalsPlansTab')) {
              this.events.publish('refresh-callplan-activity');
              this.uiService.activeView = 'callPlanDetails';
              this.uiService.showNewActivity = false;
              this.navService.popChildNavPageWithPageTracking();
              this.footerService.initButtons(FooterViews.Contacts);
            }

            this.activityService.removeActivity(activity);
            if (!this.uiService.toolsActivityActive) { this.events.publish('refreshAgenda'); }
            else this.uiService.agendaRefreshRequired = true;
            if (this.uiService.activitiesPageTab === 'day') {
              this.events.publish('activityDeleted');
            }
            this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
            // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
            // Deleted.
            // Now we need to pop out if we pushed the detail pane
            this.activityService.selectedActivity = undefined;
            if (this.activityService.selectedActivityOnAgenda && activity && this.activityService.selectedActivityOnAgenda.ID === activity.ID) {
              this.activityService.selectedActivityOnAgenda = undefined;
            }
            this.events.publish('initTodo');
          })
          .catch((err) => {
            // this.notificationService.notify("Meeting scrap failed",'Activities List','top',ToastStyle.DANGER);
          });

        return;
      }
      let popupTitle: string;
      let popupMessage: string = this.getAlertMessage(activity, accountVisitNestedMeetings.length > 0);
      let popupAction = this.translate.instant('SCRAP');
      let cancelButtonText = null;

      const isCancelEnabled = this.authService.user.buSettings && this.authService.user.buSettings['indskr_cancelprocedurelogwithreason'];
      const isCancelMeetingEnabled = this.authService.user.buSettings && this.authService.user.buSettings['indskr_cancelmeetingwithreason'];
      const isdeleteMeetingEnabled = this.authService.user.buSettings && this.authService.user.buSettings['indskr_deletemeeting'];

      if (activity instanceof SurgeryOrderActivity && activity.indskr_procedurecontract !== "") {
        this.notificationService.notify(this.translate.instant('PROCEDURE_LOG_SCRAP_NOTIFICATION'), 'procedure log', 'top', 'info');
        return;
      }
      switch (activity.type) {
        case ActivityType.Sample:
          popupTitle = this.translate.instant(activity.indskr_allocationordertype === AllocationOrderType.PromotionalMaterial ? 'SCRAP_PROMOTIONAL_MATERIAL' : 'SCRAP_ALLOCATION_ORDER');
          break;
        case ActivityType.Email:
          popupTitle = this.translate.instant('EMAIL_ACTIVITY_ALERT_TITLE_SCRAP_MESSGE');
          break;
        case ActivityType.FollowUp:
          popupTitle = this.translate.instant('SCRAP_FOLLOW_UP');
          break;
        case ActivityType.Order:
          popupTitle = this.translate.instant('SCRAP_ORDER');
          break;
        case ActivityType.SurgeryOrder:
          popupTitle = this.translate.instant('OM_SCRAP_LOG');
          if (isCancelEnabled) {
            popupTitle = this.translate.instant('DELETE_OR_CANCEL');
            popupMessage = this.translate.instant('DELETE_OR_CANCEL_PROCEDURE_LOG');
            popupAction = this.translate.instant('DELETE');
          }
          break;
        case ActivityType.ProcedureTracker:
          popupTitle = this.translate.instant('OM_SCRAP_TRACKER');
          break;
        case ActivityType.PhoneCall:
          popupTitle = this.translate.instant('SCRAP_PHONECALL');
          break;
        case ActivityType.CaseIntake:
          popupTitle = this.translate.instant('POP_SCRAP_INQUIRY');
          popupMessage = this.translate.instant('POP_R_U_SURE_SCRAP_SELECTED_INQUIRY');
          break;
        case ActivityType.StoreCheck:
          popupTitle = this.translate.instant('SCRAP_STORE_CHECK');
          popupMessage = this.translate.instant('SCRAP_STORE_CHECK_CONFIRMATION_MSG');
          break;
        case ActivityType.SetBooking:
          popupTitle = this.translate.instant('SCRAP_KIT_BOOKING');
          popupMessage = this.translate.instant('SCRAP_KIT_BOOKING_CONFIRMATION_MSG');
          break;
        case ActivityType.MedicalInquiry:
          popupTitle = this.translate.instant('POP_SCRAP_INQUIRY');
          popupMessage = this.translate.instant('POP_R_U_SURE_SCRAP_SELECTED_INQUIRY');
          break;
        default: {
          const isAccountVisitRecordWithNestedMeetings = accountVisitRecordCheckResponse.isAccountVisitNestedMeeting
            && accountVisitNestedMeetings.length > 0;
          popupTitle = isAccountVisitRecordWithNestedMeetings ? this.translate.instant('SCRAP_ACCOUNT_VISIT') : this.translate.instant('SCRAP_MEETING');

          if ((isCancelMeetingEnabled && isdeleteMeetingEnabled) || (!isCancelMeetingEnabled && isdeleteMeetingEnabled)) {
            popupTitle = this.translate.instant('DELETE_OR_CANCEL');
            popupMessage = this.translate.instant('DELETE_OR_CANCEL_MEETING');
            popupAction = this.translate.instant('DELETE_PROCEDURE_LOG_BTN_TEXT');
            cancelButtonText = this.translate.instant('CANCEL_PROECEUDURE_LOG_BTN_TEXT');
          }

          if (scrapStatus && scrapStatus == ActivityScrapStatus.HASOPENINMEETINGALLOCATIONS) {
            // HASOPENINMEETINGALLOCATIONS does not distinguish promotional material from samples
            // (only the Completed branch does), so name what is actually about to be scrapped.
            const draftOrders = this.activityService.sampleActivities
              .filter(o => o.appointmentID === activity.ID && o.statusString !== 'Completed' && o.state !== 2);
            const draftMessages: string[] = [];
            if (draftOrders.some(o => !o.isPromoMaterial)) {
              draftMessages.push(this.translate.instant('DRAFT_MEETING_HAS_OPEN_ALLOCATIONS_POPUP_MESSAGE'));
            }
            if (draftOrders.some(o => o.isPromoMaterial)) {
              draftMessages.push(this.translate.instant('DRAFT_MEETING_HAS_OPEN_PROMO_MATERIALS_POPUP_MESSAGE'));
            }
            const draftMessage = draftMessages.length
              ? draftMessages.join(' ')
              : this.translate.instant('DRAFT_MEETING_HAS_OPEN_ALLOCATIONS_POPUP_MESSAGE');
            // Append rather than replace, so the "delete or cancel" prompt survives —
            // same treatment the SDR sentence below already gets.
            popupMessage = popupMessage ? popupMessage + ' ' + draftMessage : draftMessage;
          }

          // OMNI-61316 AC1: a meeting with an open (Draft, not yet finalized) SDR gets
          // a soft warning appended, mirroring the open-allocation treatment above —
          // does not block scrap (the HASSUBMITTEDSDR check earlier in this method
          // already hard-blocks a SUBMITTED SDR; this is the separate, non-blocking
          // open case). No new ActivityScrapStatus value needed — this calls the
          // predicate directly, same as the existing hasSdrForMeeting check nearby.
          if (activity instanceof AppointmentActivity && this.sampleDeliveryRequestService.hasOpenSdrForMeeting(activity.ID)) {
            popupMessage = popupMessage
              ? popupMessage + ' ' + this.translate.instant('DRAFT_MEETING_HAS_OPEN_SDR_POPUP_MESSAGE')
              : this.translate.instant('DRAFT_MEETING_HAS_OPEN_SDR_POPUP_MESSAGE');
          }

          if (activity instanceof AppointmentActivity && this.meetingExpenseService.hasDraftExpenses(activity.ID)) {
            popupMessage = popupMessage
              ? popupMessage + ' ' + this.translate.instant('DRAFT_MEETING_HAS_OPEN_EXPENSES_POPUP_MESSAGE')
              : this.translate.instant('DRAFT_MEETING_HAS_OPEN_EXPENSES_POPUP_MESSAGE');
          }
          break;
        }
      }

      this.alertService.showAlert({
        header: popupTitle,
        message: popupMessage
      }, popupAction, cancelButtonText
      ).then(async (res) => {

        if (isCancelEnabled && activity.type == ActivityType.SurgeryOrder && res.role === 'cancel') {
          this.cancelSurgeryOrder(activity);
          return;
        }

        if ((isCancelMeetingEnabled && isdeleteMeetingEnabled && activity.type == ActivityType.Appointment && res.role === 'cancel')) {
          this.cancelMeeting(activity);
          return;
        }
        if ((!isCancelMeetingEnabled && isdeleteMeetingEnabled && activity.type == ActivityType.Appointment && res.role === 'cancel')) {
          this.scrapOrCancelWithOutReason(activity, scrapStatus, accountVisitRecordCheckResponse);
          return;
        }

        if (res.role == "ok") {
          this.activityService.selectedActivityAtHome = undefined;
          if (activity.type == ActivityType.Sample) {
            if (this.device.isBackgroundUploadInProgress && this.activityService.hasOfflineSampleOrderData(activity.ID)) return;
            this.uiService.displayLoader();
            let payload: CreateSampleDropRequestBody = new CreateSampleDropRequestBody(activity);
            payload.statecode = 2;
            payload.statuscode = 3;
            (activity as SampleActivity).state = 2;
            (activity as SampleActivity).status = 3;
            this.sampleDataService.updateSampleActivity(payload, activity).then(() => {
              this.events.publish("allocationOrderActivityDeleted", activity);
              if ((activity as SampleActivity).appointmentID && this.activityService.samplingToInMeetingActivityMapping.has((activity as SampleActivity).appointmentID)) {
                this.activityService.samplingToInMeetingActivityMapping.delete((activity as SampleActivity).appointmentID);
              }
              if (this.navService.getCurrentPageName() === PageName.NewSampleActivityComponent && this.device.isMobileDevice) {
                this.navService.popWithPageTracking();
              }
              if (this.navService.getCurrentMasterPageName() == PageName.ContactPageComponent) {
                this.activityService.selectedActivity = undefined;
                this.uiService.showNewActivity = false;
                this.navService.popChildNavPageWithPageTracking();
                this.footerService.initButtons(FooterViews.Contacts);
              }
              if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
                // if (this.footerService.isActivitiesButton &&  this.uiService.showRightPane) {
                this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
                if (this.footerService.isActivitiesButton) {
                  this.uiService.showRightPane = false;
                  this.events.publish('scrollToDate');
                } else {
                  this.events.publish("go-back-to-xperiences", PageName.GoAppActivitiesPageComponent);
                }
              }
              this.activityService.finalizeAgendaRefresh();
              this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
              // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
              if (this.activityService.selectedActivity && activity && this.activityService.selectedActivity.ID === activity.ID) {
                this.activityService.selectedActivity = undefined;
                this.activityService.selectedActivityOnAgenda = undefined;
                this.uiService.activeView = undefined;
              }
              if (this.navService.getCurrentMasterPageName() == PageName.CallPlanComponent || this.navService.getCurrentMasterPageName() == PageName.CustomerCallPlanPage) {
                this.uiService.showNewActivity = false;
                this.uiService.activeView = 'callPlanDetails';
                this.callplanService.callPlanPageView = 'CallPlanDetail';
                this.navService.popChildNavPageWithPageTracking();
              }
              this.uiService.dismissLoader();
              return;
            }).catch(() => {
              this.uiService.dismissLoader();
              // this.notificationService.notify("Allocation scrap failed",'Activities List','top',ToastStyle.DANGER);
              return;
            })
          }
          else if (activity.type == ActivityType.Email) {
            this.uiService.displayLoader();
            if (this.device.isBackgroundUploadInProgress && this.activityService.hasOfflineEmailData(activity.ID)) return;
            this.emailService.scrapEmailActivity(activity).then(() => {
              if (this.navService.getCurrentPageName() === PageName.EmailDetailsPageComponent && this.device.isMobileDevice) {
                this.navService.popWithPageTracking();
              }
              if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent && this.uiService.showRightPane) {
                this.uiService.showRightPane = false;
                this.events.publish('scrollToDate');
                this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
              }
              this.activityService.removeActivity(activity);
              if (!this.uiService.toolsActivityActive) {
                this.events.publish('refreshAgenda');

              } else {
                this.uiService.agendaRefreshRequired = true;
              }
              if (this.uiService.activitiesPageTab === 'day') {
                this.events.publish('activityDeleted', this.activityService.selectedActivity);
              }
              this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
              // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
              if (this.activityService.selectedActivity && activity && this.activityService.selectedActivity.ID === activity.ID) {
                this.activityService.selectedActivity = undefined;
                this.activityService.selectedActivityOnAgenda = undefined;
                this.uiService.activeView = undefined;
                this.events.publish('scrollToDate');
                this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
              }
              this.uiService.dismissLoader();
              return;
            }).catch(() => {
              this.uiService.dismissLoader();
              return;
            });
          }
          else if (activity.type === ActivityType.FollowUp) {
            this.uiService.displayLoader();
            if (this.device.isBackgroundUploadInProgress && activity.pendingPushToDynamics) return;
            let id = activity.ID;
            (<FollowUpActivity>activity).pendingPushToDynamics = true;
            this.followUpDataService.scrapFollowUpActivity({ onDynamics: !this.device.isOffline, onLocalDatabase: true, onLocalCopy: true }, (<FollowUpActivity>activity)).then(async succ => {
              this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
              this.events.publish('followupActionDeleted', activity);
              this.closeFollowUpDetailPageAfterOperation();
              this.uiService.dismissLoader();
            }).catch(err => {
              this.uiService.dismissLoader();
              //Handle error scenario
            })
          } else if (activity.type == ActivityType.Order || activity.type == ActivityType.ProcedureTracker) {
            this.uiService.displayLoader();
            if (this.device.isBackgroundUploadInProgress && activity.pendingPushToDynamics) return;
            (<OrderActivity>activity).pendingPushToDynamics = true;
            this.orderActivityDataService.updateOrderActivityStatus({ onDynamics: !this.device.isOffline, onLocalDatabase: true, onLocalCopy: true, operationDetail: { code: 'scraporderactivity', message: 'Scrap Order' } }, (<OrderActivity>activity)).then(async succ => {
              if (!this.uiService.toolsActivityActive) {
                this.events.publish('refreshAgenda');
              } else {
                this.uiService.agendaRefreshRequired = true;
              }

              this.uiService.activeView = '';
              this.uiService.showRightPane = false;
              if (this.uiService.activitiesPageTab === 'day') {
                this.events.publish('activityDeleted', activity);
              }
              this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
              // if (activity.type == ActivityType.SurgeryOrder) { this.events.publish('surgeryOrderActivityDeleted', activity); }
              if (activity.type == ActivityType.ProcedureTracker) { this.events.publish('procedureTrackerActivityDeleted', activity); }
              // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
              if (this.activityService.selectedActivity && this.activityService.selectedActivity.ID == activity.ID) {
                this.activityService.selectedActivity = null;
                this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, null, {
                  deviceString: this.device.deviceFlags.ios ? 'ios' : 'android'
                });
              }
            }).catch(err => {
              //Handle error scenario
            });
            this.uiService.dismissLoader();
          } else if (activity.type == ActivityType.SurgeryOrder) {
            if (activity.indskr_procedurecontract) return;
            this.uiService.displayLoader();
            if (this.device.isBackgroundUploadInProgress && activity.pendingPushToDynamics) return;
            (<SurgeryOrderActivity>activity).pendingPushToDynamics = true;
            const isSelected = this.activityService.selectedActivity && this.activityService.selectedActivity.ID == activity.ID;
            await this.surgeryOrderService.updateOrderActivityStatus({
              onDynamics: !this.device.isOffline,
              onLocalDatabase: true,
              onLocalCopy: true,
              operationDetail: { code: 'scrapsurgeryorderactivity', message: this.translate.instant('OM_UPDATE_O_ACTIVITY'), }
            }, (<SurgeryOrderActivity>activity)).then((success) => {
              if (!this.uiService.toolsActivityActive) {
                this.events.publish('refreshAgenda');
              } else {
                this.uiService.agendaRefreshRequired = true;
              }
              this.uiService.activeView = '';
              this.uiService.showRightPane = false;
              if (this.uiService.activitiesPageTab === 'day') {
                this.events.publish('activityDeleted', activity);
              }
              this.activityService.publishActivityEvent({ action: "Delete", activity: (<SurgeryOrderActivity>activity) });
              if (isSelected) {
                this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, null, {
                  deviceString: this.device.deviceFlags.ios ? 'ios' : 'android'
                });
              }
              this.uiService.dismissLoader();
            }).catch(err => {
              //Handle error scenario
            });
          }
          else if (activity.type == ActivityType.PhoneCall) {
            // (<PhoneActivity>activity).pendingPushToDynamics = true;
            if (this.device.isBackgroundUploadInProgress && this.activityService.hasOfflinePhoneCallData(activity.ID)) return;
            this.deletePhoneCall(activity)
          }
          else if (activity.type == ActivityType.CaseIntake) {
            this.uiService.displayLoader()
            await this.caseManagementService.scrapCustomerInquiry(activity as CaseActivity);
            this.closeActivityDetailsPane();
            this.uiService.dismissLoader();
          } else if (activity.type === ActivityType.StoreCheck) {
            if (this.device.isOffline) {
              this.notificationService.notify(this.translate.instant('YOUR_DEVICE_IS_OFFLINE'), 'Store Check Activity', 'top', ToastStyle.DANGER);
              return;
            }
            await this.uiService.displayLoader();
            await this.storeCheckService.scrapStoreCheck(activity);
            this.storeCheckService.setCurrentStoreCheckActivity(null);
            this.activityService.finalizeAgendaRefresh();
            this.activityService.publishActivityEvent({ action: 'Delete', activity });
            if (this.activityService.selectedActivity?.ID == activity.ID) {
              this.uiService.showRightPane = false;
              this.activityService.selectedActivity = null;
              this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
            }
            await this.uiService.dismissLoader();
          } else if (activity.type === ActivityType.MedicalInquiry) {
            await this.scrapMedicalInquiryFromAgenda(activity as MedicalInquiry);
          } else if (activity.type === ActivityType.SetBooking) {
            if (this.device.isOffline) {
              this.notificationService.notify(this.translate.instant('YOUR_DEVICE_IS_OFFLINE'), 'Store Check Activity', 'top', ToastStyle.DANGER);
              return;
            }
            await this.uiService.displayLoader();
            const action: OperationDetail = {
              onDynamics: true,
              onLocalDatabase: true,
              onLocalCopy: true,
              operationDetail: {
                code: 'scrapsetbooking',
                message: 'Scrap Set Booking Activity'
              }
            };
            let draftkitbookingscrapflow: boolean = false;
            if ((activity as SetBookingActivity).indskr_status == SetBookingStatus.DRAFT) {
              draftkitbookingscrapflow = true;
              (activity as SetBookingActivity).status = 1;
              (activity as SetBookingActivity).serviceDTO = {
                indskr_setbookingid: activity.ID,
                statecode: 1
              };
            }
            else {
              (activity as SetBookingActivity).indskr_status = SetBookingStatus.PENDINGCANCEL;
              (activity as SetBookingActivity).statusString = "Pending Cancel";
              (activity as SetBookingActivity).serviceDTO = {
                indskr_setbookingid: activity.ID,
                indskr_status: SetBookingStatus.PENDINGCANCEL,
              };
            }
            await this.setBookingDataService.updateSetBookingActivity(action, [activity], new Date().getTime()).then(async (res) => {
              if (draftkitbookingscrapflow) {
                this.activityService.removeActivity(activity);
              }
              this.activityService.finalizeAgendaRefresh();
              if (!draftkitbookingscrapflow && this.authService.user.buSettings['indskr_displaycancelledkitbookingoncalendar']) {
                if (forcePopup) this.navService.popChildNavPageWithPageTracking();
                this.events.publish('initSetBookingDisplayForm');
              } else {
                if (this.navService.getActiveChildNavViewPageName() == PageName.SetBookingActivityDetailsComponent) {
                  this.uiService.showRightPane = false;
                  this.activityService.selectedActivity = null;
                  this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
                }
                if (this.navService.getActiveChildNavViewPageName() == PageName.DynamicFormComponent) {
                  this.uiService.showRightPane = false;
                  this.activityService.selectedActivity = null;
                  this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
                }
              }
              this.uiService.dismissLoader();
            });
          }
          else {
            if ((isdeleteMeetingEnabled && activity.type == ActivityType.Appointment)) {
              this.deleteMeetingnewFlow(activity, scrapStatus)
              return;
            }
            if ((isCancelMeetingEnabled && !isdeleteMeetingEnabled && activity.type == ActivityType.Appointment)) {
              this.cancelMeeting(activity);
              return;
            }
            this.scrapOrCancelWithOutReason(activity, scrapStatus, accountVisitRecordCheckResponse);
          }
        }
      });
    }
  }

  /**
   * Agenda popover scrap: delegates data deactivation + agenda list removal + events to service;
   * component handles agenda-page-specific UI cleanup (selectedActivity, RHS pane, day-view event).
   */
  private async scrapMedicalInquiryFromAgenda(mi: MedicalInquiry): Promise<void> {
    await this.uiService.displayLoader();
    try {
      await this.medicalInquiryService.scrapMedicalInquiryAndSync(mi);
      this.medicalInquiryService.setSelectedMI(null);
      if (this.uiService.activitiesPageTab === 'day') {
        this.events.publish('activityDeleted', mi);
      }
      if (this.activityService.selectedActivity?.ID === mi.ID) {
        this.activityService.selectedActivity = undefined;
        this.activityService.selectedActivityOnAgenda = undefined;
        this.uiService.activeView = undefined;
        if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
          this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
          if (this.footerService.isActivitiesButton) {
            this.uiService.showRightPane = false;
          }
        }
      }
    } catch (err) {
      console.error('GoAppActivitiesPage: scrap MI failed', err);
      this.notificationService.notify(this.translate.instant('FAILED'), 'Activities', 'top', ToastStyle.DANGER, 3000);
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  async deleteMeetingnewFlow(activity, scrapStatus?: ActivityScrapStatus) {
    const shouldCallDeleteApi = !this.device.isOffline &&
      !this.activityService.hasOfflineMeetingData(activity.ID) &&
      !activity.ID.includes('offline');
    if (shouldCallDeleteApi) {
      this.uiService.displayLoader();
      // Dynamics cascade-restricts on both indskr_sampledrop_AppointmentId and the
      // SDR→appointment FK — scrap both before the REST DELETE so Dynamics accepts it.
      if (scrapStatus === ActivityScrapStatus.HASOPENINMEETINGALLOCATIONS) {
        await this._scrapDraftAllocationOrdersInMeeting(activity);
      }
      await this._scrapDraftSdrsInMeeting(activity);
      await this._scrapDraftExpensesInMeeting(activity);
      await this.activityDataService.deleteMeeting(activity.ID).then(async (success) => {
        await this.activityService.removeActivity(activity).catch(err => {
          this.uiService.dismissLoader();
          console.error('deleteActivity: ', err);
        });
        if (!this.uiService.toolsActivityActive) {
          this.events.publish('refreshAgenda');
        } else this.uiService.agendaRefreshRequired = true;
        this.activityService.selectedActivity = null;
        if (this.uiService.activitiesPageTab === 'day' || this.uiService.activitiesPageTab === 'plans') {
          this.events.publish('activityDeleted', activity);
        }
        this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
        if (this.activityService.selectedActivityOnAgenda && activity && this.activityService.selectedActivityOnAgenda.ID === activity.ID) {
          this.activityService.selectedActivityOnAgenda = undefined;
        }
        this.closeActivityDetailsPane();
      }).catch(err => {
        this.notificationService.notify(this.translate.instant('MEETING_DELETE_FAILED'), 'Activities', 'top', ToastStyle.DANGER, 3000);
      }).finally(() => {
        this.uiService.dismissLoader();
      });
    } else {
      this.uiService.displayLoader();
      if (scrapStatus === ActivityScrapStatus.HASOPENINMEETINGALLOCATIONS) {
        await this._scrapDraftAllocationOrdersInMeeting(activity);
      }
      await this._scrapDraftSdrsInMeeting(activity);
      await this._scrapDraftExpensesInMeeting(activity);
      this.meetingService.updateMeetingStatus(activity, MeetingStatus.CANCELED, null, true)
        .then(async () => {
          await this.activityService.removeActivity(activity).catch(err => {
            this.uiService.dismissLoader();
            console.error('deleteActivity: ', err);
          });
          if (!this.uiService.toolsActivityActive) {
            this.events.publish('refreshAgenda');
          } else this.uiService.agendaRefreshRequired = true;
          this.activityService.selectedActivity = null;
          if (this.uiService.activitiesPageTab === 'day' || this.uiService.activitiesPageTab === 'plans') {
            this.events.publish('activityDeleted', activity);
          }
          this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
          if (this.activityService.selectedActivityOnAgenda && activity && this.activityService.selectedActivityOnAgenda.ID === activity.ID) {
            this.activityService.selectedActivityOnAgenda = undefined;
          }
          this.closeActivityDetailsPane();
        })
        .catch((err) => {
          this.notificationService.notify(this.translate.instant('MEETING_DELETE_FAILED'), 'Activities', 'top', ToastStyle.DANGER, 3000);
        })
        .finally(() => {
          this.uiService.dismissLoader();
        });
    }
  }

  private closeFollowUpDetailPageAfterOperation(): void {
    let activChildNav: ChildNavNames = this.navService.getActiveChildNavName();
    if (this.followUpDataService.inMeetingFollowupActionActivity) return;
    switch (activChildNav) {
      case ChildNavNames.ActivitiesPageNavigation:
        this.activityService.publishActivityEvent({ action: "Delete", activity: this.activityService.selectedActivity });
        this.activityService.selectedActivity = null;
        if (this.uiService.activitiesPagePlanTabSelectedPlan === 'scientificPlans' || this.uiService.activitiesPagePlanTabSelectedPlan === 'accountPlansTab' || this.uiService.activitiesPagePlanTabSelectedPlan === 'goalsPlansTab') {
          this.navService.popChildNavPageWithPageTracking().then(() => {
            this.events.publish("refreshFollowup");
          });
          this.uiService.activeView = 'ActivitiesPageRightPaneNav';
          this.footerService.initButtons('');
        }
        else if (this.footerService.isActivitiesButton) {
          this.uiService.activeView = '';
          this.uiService.showRightPane = false;
          if (this.uiService.activitiesPageTab === 'day') {
            this.events.publish('activityDeleted', this.activityService.selectedActivity);
          }
          this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
        } else {
          this.navService.popChildNavPageWithPageTracking();
          this.events.publish("go-back-to-xperiences", PageName.FollowUpActionDetailComponent);
        }
        break;
      case ChildNavNames.AccountManagementNavigation:
        this.navService.popChildNavPageWithPageTracking();
        if (this.navService.getActiveChildNavViewPageName() == PageName.NewActivityComponent) {
          this.navService.popChildNavPageWithPageTracking();
        }
        this.activityService.selectedActivity = null;
        this.footerService.initButtons(FooterViews.AccountPlanObjectives)
        break;
      case ChildNavNames.ContactPageNavigation:
        this.navService.popChildNavPageWithPageTracking();
        this.footerService.initButtons(FooterViews.Contacts)
        break;
      case ChildNavNames.AccountPageNavigation:
        this.navService.popChildNavPageWithPageTracking();
        this.footerService.initButtons(FooterViews.Accounts)
        break;
      case ChildNavNames.ScientificActivityNavigation:
        this.navService.popChildNavPageWithPageTracking().then(() => {
          this.events.publish("refreshFollowup");
        });
        this.footerService.initButtons('');
        break;
      case ChildNavNames.OpportunityManagementNavigation:
        this.navService.popChildNavPageWithPageTracking();
        this.footerService.initButtons('');
        break;
      case ChildNavNames.MarketingPlanNavigation:
        this.navService.popChildNavPageWithPageTracking();
        this.footerService.initButtons('');
        break;
    }
    this.activityService.finalizeAgendaRefresh();
  }

  private async _scrapDraftAllocationOrdersInMeeting(activity: Activity): Promise<void> {
    if (activity.type !== ActivityType.Appointment || !activity.ID) return;
    const scraps = this.activityService.sampleActivities
      .filter(o => o.appointmentID === activity.ID && o.statusString !== 'Completed' && o.state !== 2)
      .map(sampleOrder => {
        const payload = new CreateSampleDropRequestBody(sampleOrder);
        payload.statecode = 2;
        payload.statuscode = 3;
        sampleOrder.state = 2;
        sampleOrder.status = 3;
        return this.sampleDataService.updateSampleActivity(payload, sampleOrder);
      });
    await Promise.all(scraps).catch(() => {});
  }

  private async _scrapDraftExpensesInMeeting(activity: Activity): Promise<void> {
    if (activity.type !== ActivityType.Appointment || !activity.ID) return;
    if (!this.meetingExpenseService.hasDraftExpenses(activity.ID)) return;
    await this.meetingExpenseService.scrapDraftsForMeeting(activity.ID).catch(() => {});
  }

  private async _scrapDraftSdrsInMeeting(activity: Activity): Promise<void> {
    if (activity.type !== ActivityType.Appointment || !activity.ID) return;
    const openSdrs = (this.sampleDeliveryRequestService.allSdrs || [])
      .filter(s => s.appointmentId === activity.ID && s.statusCode === SdrStatus.Active);
    await Promise.all(openSdrs.map(sdr => this.sampleDeliveryRequestService.scrapSdr(sdr).catch(() => {})));
  }

  async scrapOrCancelWithOutReason(activity, scrapStatus, accountVisitRecordCheckResponse) {
    if (this.device.isBackgroundUploadInProgress && this.activityService.hasOfflineMeetingData(this.activityService.selectedActivity.ID)) return;
    this.uiService.displayLoader();
    if (scrapStatus && scrapStatus == ActivityScrapStatus.HASOPENINMEETINGALLOCATIONS) {
      await this._scrapDraftAllocationOrdersInMeeting(activity).catch(() => {});
    }
    await this._scrapDraftSdrsInMeeting(activity);
    await this._scrapDraftExpensesInMeeting(activity);
    if (this.activityService.selectedActivity) {
      this.websocket.sendRemoteMeetingEnd(this.activityService.selectedActivity, true);
      this.websocket.removeSubscription(this.activityService.selectedActivity);
      this.websocket.meetingInProgress = false;
    }

    if (
      accountVisitRecordCheckResponse.isAccountVisitRecord
      || accountVisitRecordCheckResponse.isAccountVisitNestedMeeting
    ) {
      await this.scrapAccountVisit(activity);
    } else {
      await this.meetingService.updateMeetingStatus(activity, MeetingStatus.CANCELED).catch((err) => {
        this.uiService.dismissLoader();
        console.error('deleteActivity: ', err);
      });
    }
    if (this.authService.user.buSettings &&
      !this.authService.user.buSettings['indskr_displaycancelledmeetingoncalendar']) {
      await this.activityService.removeActivity(activity).catch(err => {
        this.uiService.dismissLoader();
        console.error('deleteActivity: ', err);
      });
      this.activityService.selectedActivity = null;
      if (this.uiService.activitiesPageTab === 'day' || this.uiService.activitiesPageTab === 'plans') {
        this.events.publish('activityDeleted', activity);
      }
      this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
      // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
      if (this.activityService.selectedActivityOnAgenda && activity && this.activityService.selectedActivityOnAgenda.ID === activity.ID) {
        this.activityService.selectedActivityOnAgenda = undefined;
      }
      this.closeActivityDetailsPane();
      this.uiService.dismissLoader();
    }
    if (!this.uiService.toolsActivityActive) {
      this.events.publish('refreshAgenda');
    } else this.uiService.agendaRefreshRequired = true;
    this.events.publish("detectChangesOnActivityDetails");
    this.footerService.initButtons(FooterViews.Activities);
    this.events.publish('initTodo');
    this.uiService.dismissLoader();
  }

  private closeActivityDetailsPane() {
    if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent && this.activityService.prevSelectedActivity instanceof SetBookingActivity) {
      this.navService.popChildNavPageWithPageTracking();
      this.uiService.activeView = 'ActivitiesPageRightPaneNav';
      this.activityService.selectedActivity = this.activityService.prevSelectedActivity;
      this.activityService.prevSelectedActivity = undefined;
      this.events.publish('refresh-kit-booking-activities');
    } else if (this.navService.getCurrentPageName() === PageName.ActivitiesDetailsPaneComponent && this.device.isMobileDevice) {
      this.navService.popWithPageTracking();
    } else if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) {
      if (this.footerService.isActivitiesButton) { // && this.uiService.showRightPane) {
        this.uiService.showRightPane = false;
        this.events.publish('scrollToDate');
        if (this.uiService.activitiesPageTab != 'plans') {
          this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
        }
        else {
          this.navService.popChildNavPageWithPageTracking().then(() => {
            this.footerService.initButtons('');
          });
        }
      }
      else this.events.publish("go-back-to-xperiences", PageName.GoAppActivitiesPageComponent);
    }
    if (this.navService.getCurrentPageName() == PageName.ContactPageComponent) {
      if (this.uiService.showCancelDoneOnActivityDetails) {
        this.uiService.showCancelDoneOnActivityDetails = false;
      }
      this.uiService.timelineRefreshRequired = true;
      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'contactDetails';
      this.navService.popChildNavPageWithPageTracking();
      this.footerService.initButtons(FooterViews.Contacts);
    } else if (this.navService.getCurrentMasterPageName() == PageName.AccountManagementPageComponent && this.navService.getActiveChildNavViewPageName() == PageName.ActivitiesDetailsPaneComponent) {
      this.footerService.initButtons("accountPlansDetail");
      this.navService.popChildNavPageWithPageTracking();
    } else if (this.navService.getCurrentMasterPageName() == PageName.AccountPageComponent &&
      this.navService.getActiveChildNavViewPageName() == PageName.ActivitiesDetailsPaneComponent) {
      this.navService.popChildNavPageWithPageTracking();
    } else if (this.navService.getCurrentMasterPageName() == PageName.ScientificActivityPage) {
      this.footerService.initButtons('');
      this.navService.popChildNavPageWithPageTracking();
    } else if (this.navService.getCurrentMasterPageName() == PageName.CallPlanComponent || this.navService.getCurrentMasterPageName() == PageName.CustomerCallPlanPage) {
      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'callPlanDetails';
      this.callplanService.callPlanPageView = 'CallPlanDetail';
      this.events.publish('activity-scrapped-created-from-call-plan');
      this.navService.popChildNavPageWithPageTracking();
    } else if (this.navService.getCurrentMasterPageName() == PageName.GoAppActivitiesPageComponent && this.uiService.activitiesPageTab == 'plans') {
      // this.footerService.initButtons('');
      // this.navService.popChildNavPageWithPageTracking();
      // this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    } else if (this.navService.getCurrentMasterPageName() == PageName.OpportunityManagementPage) {
      this.footerService.initButtons('');
      this.navService.popChildNavPageWithPageTracking();
    } else if (this.navService.getCurrentMasterPageName() == PageName.MarketingPlanManagementPageComponent) {
      this.footerService.initButtons('');
      this.navService.popChildNavPageWithPageTracking();
    }
  }

  private async scrapAccountVisit(activity: AppointmentActivity): Promise<boolean> {
    let success = false;

    const payload: AppointmentActivity[] = [];
    if (activity.indskr_isparentcall) {
      // Parent Account Visit
      // Scrap parent
      payload.push(new AppointmentActivity({
        _id: activity._id ?? DB_KEY_PREFIXES.MEETING_ACTIVITY + activity.ID,
        _rev: activity._rev ?? undefined,
        activityid: activity.ID,
        offlineMeetingId: activity.offlineMeetingId,
        subject: activity.subject,
        scheduledstart: activity.scheduledStart,
        scheduledend: activity.scheduledEnd,
        indskr_isparentcall: true,
        stateCode: 2,
      }));

      const nestedMeetings = this.activityService.getAccountVisitNestedMeetings(activity.ID);
      if (
        Array.isArray(nestedMeetings)
        && nestedMeetings.length > 0
      ) {
        // Scrap children
        payload.push(
          ...nestedMeetings.map(a => {
            return new AppointmentActivity({
              _id: a._id ?? DB_KEY_PREFIXES.MEETING_ACTIVITY + a.ID,
              _rev: a._rev ?? undefined,
              activityid: a.ID,
              offlineMeetingId: a.offlineMeetingId,
              subject: a.subject,
              scheduledstart: a.scheduledStart,
              scheduledend: a.scheduledEnd,
              indskr_parentcallid: a.indskr_parentcallid,
              stateCode: 2,
            });
          })
        );
      }
    } else if (activity.indskr_parentcallid) {
      // Scrap nested meeting
      payload.push(
        new AppointmentActivity({
          _id: activity._id ?? DB_KEY_PREFIXES.MEETING_ACTIVITY + activity.ID,
          _rev: activity._rev ?? undefined,
          activityid: activity.ID,
          offlineMeetingId: activity.offlineMeetingId,
          subject: activity.subject,
          scheduledstart: activity.scheduledStart,
          scheduledend: activity.scheduledEnd,
          indskr_parentcallid: activity.indskr_parentcallid,
          stateCode: 2,
        })
      );

      // Remove contact from parent
      // Since the crud function requires fully loaded activity object as payload,
      // we have to make sure to append the meeting details to the activity object.
      const parent = await this.activityDataService
        .getNewDetailAppendedAppointmentActivityInstanceFromLocalDB(activity.indskr_parentcallid);
      if (
        !parent
        || !parent.contacts
      ) {
        console.error('scrapAccountVisit: Could not find the parent account visit or contact is not available.', activity, parent);
        return success;
      }

      // ! meed
      const contact = Array.isArray(activity.contacts) ? activity.contacts[0] : undefined;
      if (contact) {
        const contactIdx = parent.contacts?.findIndex(c => c.ID === contact.ID) ?? -1;
        if (contactIdx >= 0) {
          parent.contacts.splice(contactIdx, 1);
        }
      }
      payload.unshift(parent);
    } else {
      console.error('scrapAccountVisit: not an account visit?', activity);
    }

    try {
      success = payload.length > 0 ? await this.meetingService.crudMeetingsOnlineWithOfflineUploadEndpoint(payload) : false;
    } catch (error) {
      console.error('scrapAccountVisit: ', error);
    }

    return success;
  }

  private async cancelMeeting(activity: AppointmentActivity) {
    try {
      const response = await this.activityService.showCancelMeetingPopover('');
      const isSelected = this.activityService.selectedActivity && this.activityService.selectedActivity.ID == activity.ID;
      if (response && response.role && response.role == 'ok') {
        this.uiService.displayLoader();
        if (response.selectedOtherItem) {
          activity.indskr_reasonforcancellation = response.inputs.value;
          activity.indskr_cancellationreasonother = response.selectedOtherItem
        } else {
          activity.indskr_reasonforcancellation = response.inputs.value;
          activity.cancellationreason = response.inputs.name
        }
        this.meetingService.updateMeetingStatus(activity, MeetingStatus.CANCELED)
          .then(() => {
            if (!this.uiService.toolsActivityActive) { this.events.publish('refreshAgenda'); }
            else this.uiService.agendaRefreshRequired = true;
            this.events.publish("detectChangesOnActivityDetails");
            this.events.publish('meetingCancelled', activity);
            this.footerService.initButtons(FooterViews.Activities);
            if (this.authService.user.buSettings &&
              !this.authService.user.buSettings['indskr_displaycancelledmeetingoncalendar']) {
              this.uiService.activeView = '';
              this.uiService.showRightPane = false;
              this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
              this.activityService.selectedActivity = undefined;
              if (this.activityService.selectedActivityOnAgenda && activity && this.activityService.selectedActivityOnAgenda.ID === activity.ID) {
                this.activityService.selectedActivityOnAgenda = undefined;
              }
            }
            this.uiService.dismissLoader();

          })
          .catch((err) => {
            // this.notificationService.notify("Meeting scrap failed",'Activities List','top',ToastStyle.DANGER);
          });

      }
    } catch (error) {
      console.error('Error while showing cancel popover', error);
    }
  }

  private async cancelSurgeryOrder(activity: SurgeryOrderActivity) {
    try {
      if (activity.indskr_procedurecontract) return;

      const response = await this.surgeryOrderService.showCancelSurgeryOrderPopover('');
      const isSelected = this.activityService.selectedActivity && this.activityService.selectedActivity.ID == activity.ID;
      if (response && response.role && response.role == 'ok') {
        this.uiService.displayLoader();
        if (response.selectedOtherItem) {
          activity.indskr_cancellationreasonother = response.selectedOtherItem
        } else {
          activity.indskr_reasonforcancellation = response.inputs.value;
          activity.cancellationreason = response.inputs.name
        }
        await this.surgeryOrderService.updateOrderActivityStatus({
          onDynamics: !this.device.isOffline,
          onLocalDatabase: true,
          onLocalCopy: true,
          operationDetail: { code: 'cancelsurgeryorder', message: this.translate.instant('OM_UPDATE_O_ACTIVITY'), }
        }, activity);
        if (!this.uiService.toolsActivityActive) { this.events.publish('refreshAgenda'); }
        else this.uiService.agendaRefreshRequired = true;

        if (this.authService.user.buSettings &&
          !this.authService.user.buSettings['indskr_displaycancelledprocedurelogoncalendar']) {
          this.uiService.activeView = '';
          this.uiService.showRightPane = false;
          if (this.uiService.activitiesPageTab === 'day') this.events.publish('activityDeleted', activity);
          this.activityService.publishActivityEvent({ action: "Delete", activity: (<SurgeryOrderActivity>activity) });
          if (isSelected) {
            this.activityService.selectedActivityAtHome = null;
            this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, null, {
              deviceString: this.device.deviceFlags.ios ? 'ios' : 'android'
            });
          }
        }
        this.uiService.dismissLoader();
      }
    } catch (error) {
      console.error('Error while showing cancel popover', error);
    }
  }

  private getAlertMessage(activity: any, hasAccountVisitNestedMeetings = false): string {
    let message: string;
    switch (activity.type) {
      case ActivityType.Sample:
        message = this.translate.instant(activity.indskr_allocationordertype === AllocationOrderType.PromotionalMaterial ? 'CONFIRM_SCRAP_PROMOTIONAL_MATERIAL' : 'POP_R_U_SURE_SCRAP_ALLOC_O');
        break;
      case ActivityType.Email:
        message = this.translate.instant('R_U_SURE_SCRAP_MESG');
        break;
      case ActivityType.PhoneCall:
        message = this.translate.instant('R_U_SURE_SCRAP_PHONE');
        break;
      case ActivityType.FollowUp:
        message = this.translate.instant('POP_R_U_SURE_SCRAP_FOLLOW_UP');
        break;
      case ActivityType.Order:
        message = this.translate.instant('R_U_SURE_SCRAP_O');
        break;
      case ActivityType.SurgeryOrder:
        message = this.translate.instant('R_U_SURE_SCRAP_SO');
        break;
      case ActivityType.ProcedureTracker:
        message = this.translate.instant('R_U_SURE_SCRAP_TRACKER');
        break;
      case ActivityType.MedicalInquiry:
        message = this.translate.instant('POP_R_U_SURE_SCRAP_SELECTED_INQUIRY');
        break;
      default: {
        // Appointment
        if (
          hasAccountVisitNestedMeetings
        ) {
          message = this.translate.instant('ACCOUNT_VISIT_NESTED_MEETING_WILL_BE_SCRAPPED_CONFIRM');
        } else {
          message = this.translate.instant('POP_R_U_SURE_SCRAP_MEETING');
        }
        break;
      }
    }
    return message;
  }

  @HostBinding('class.preview-active')
  get showRightPane() {
    // Must read this page's own child nav, not the active one: a master page pushed over the home
    // page (contact list, account list, ...) keeps its own NothingSelectedView-rooted nav active
    // until its ngOnDestroy, which runs after the pop transition.
    return this.uiShowRightPane
      && this.navService.getChildNavViewPageNameFor(ChildNavNames.ActivitiesPageNavigation) !== PageName.NothingSelectedView;
  }

  private deletePhoneCall(activity, force?: boolean) {

    if (!force) {
      this.uiService.displayLoader();
    }
    const newDate = new Date().getTime();

    this.phonecallDataService.updatePhonecallActivityStatus(activity, PhonecallStatus.CANCELED, newDate)
      .then(() => {
        this.events.publish('phoneCallActivityDeleted', activity);
        if ((this.navService.getCurrentMasterPageName() === PageName.AccountManagementPageComponent &&
          this.navService.getActiveChildNavViewPageName() === PageName.PhoneCallDetailsComponent) ||
          (this.navService.getCurrentMasterPageName() === PageName.AccountPageComponent &&
            this.navService.getActiveChildNavViewPageName() === PageName.PhoneCallDetailsComponent)) {
          this.uiService.dismissLoader();
          this.navService.popChildNavPageWithPageTracking();
          this.activityService.removeActivity(activity);
          this.activityService.finalizeAgendaRefresh();
          this.footerService.initButtons('');
        }
        else {

          this.activityService.removeActivity(activity);
          this.activityService.finalizeAgendaRefresh();
          if (this.uiService.activitiesPageTab === 'day') {
            this.events.publish('activityDeleted', activity);
          }
          this.activityService.publishActivityEvent({ action: "Delete", activity: activity });
          // if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
          if (!force) {
            this.uiService.dismissLoader();
            if (this.navService.getCurrentMasterPageName() === PageName.ContactPageComponent) {
              if (this.uiService.showCancelDoneOnActivityDetails) {
                this.uiService.showCancelDoneOnActivityDetails = false;
              }
              this.uiService.showNewActivity = false;
              this.uiService.activeView = 'contactDetails';
              this.navService.popChildNavPageWithPageTracking();
              this.footerService.initButtons(FooterViews.Contacts);
              return
            } else if (this.navService.getCurrentMasterPageName() === PageName.CallPlanComponent || this.navService.getCurrentMasterPageName() === PageName.CallPlanDetails || this.navService.getCurrentMasterPageName() === PageName.CustomerCallPlanPage || this.navService.getCurrentMasterPageName() === PageName.CustomerCallPlanDetails) {
              if (this.uiService.showCancelDoneOnActivityDetails) {
                this.uiService.showCancelDoneOnActivityDetails = false;
              }
              this.uiService.showNewActivity = false;
              this.uiService.activeView = 'callPlanDetails';
              this.callplanService.callPlanPageView = 'CallPlanDetail';
              this.navService.popChildNavPageWithPageTracking();
              this.footerService.initButtons(FooterViews.CallPlanDetails);
              return
            } else if (this.navService.getCurrentMasterPageName() === PageName.GoAppActivitiesPageComponent && this.uiService.activitiesPageTab === 'plans') {
              this.uiService.activeView = 'ActivitiesPageRightPaneNav';
              this.navService.popChildNavPageWithPageTracking();
              this.footerService.initButtons('');
              return
            }
            this.uiService.activeView = '';
            this.uiService.showRightPane = false;
            if (this.activityService.selectedActivity && this.activityService.selectedActivity.ID == activity.ID) {
              this.activityService.selectedActivity = null;
              this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView);
            }

          }
        }
      }).catch(err => {
        //Handle error scenario
        if (!force) {
          this.uiService.dismissLoader();
        }
      });
  }

  ngAfterViewInit() {
    this.isMobilePotrait = window.innerWidth <= 500;
    this.navService.initRightPaneChildNav(this.navCtrl, ChildNavNames.ActivitiesPageNavigation, PageName.GoAppActivitiesPageComponent, false, { deviceString: this.device.deviceFlags.ios ? 'ios' : 'other' });
    this.openRetainedSelectedActivity();
  }

  onOptionSelection(selectedView) {
    if (selectedView === 'more') return;
    this.selectedView = selectedView;
  }

  private openRetainedSelectedActivity() {
    if (!this.retainSelectedActivity || !this.activityService.selectedActivity) {
      return;
    }
    setTimeout(() => {
      this.onActivityOpenedFromActivities({ activity: this.activityService.selectedActivity, isInboundFlow: false, isNewlyCreatedActivity: false, fetchData: true});
    }, 1);
  }

  private hasMetricsFeaturesMapped() {
    const edgeAnalyticsEnabled = this.authService.hasFeatureAction(FeatureActionsMap.EDGE_ANALYTICS_MEETING); 
    return this.authService.hasFeatureAction(FeatureActionsMap.CALL_PLANS) 
    || this.authService.hasFeatureAction(FeatureActionsMap.OPPORTUNITY_MANAGEMENT)
    || edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.BASELINE) 
    || edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.MESSAGE_ACTIVITY) 
    || edgeAnalyticsEnabled && this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TOOL)
    || this.authService.user.hasProcedureLog
    || this.authService.hasFeatureAction(FeatureActionsMap.SALES_DATA_REPORT);
  }

  onActivityOpenedFromActivities(event: any) {
    this.activityService.activityOpenedFromTodo = false;
    this.activityOpenedFrom = 'activities';
    this.openActivityDetails(event.activity, event.isInboundFlow, event.isNewlyCreatedActivity, event.fetchData);
  }

  onActivityOpenedFromTodo() {
    this.activityOpenedFrom = 'todo';
    if (this.activityService.newHomeView === 'calendarView') {
      this.goAppActivitiesComponent?.activityCalendar?.updateViewType(new Date());
    }
  }

  async openActivityDetails(activity, isInboundFlow = false, isNewlyCreatedActivity = false, fetchData = true) {
    let flag = await this.setBookingDataService.checkSetBookingDiscardConfirmationRequired().catch(err => { });
    if (flag) {
      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'ActivitySkeleton';
      this._cd.detectChanges();
      setTimeout(() => {
        this.handleOpenActivity(activity, isInboundFlow, isNewlyCreatedActivity, fetchData);
      }, 1)
    }
  }

  public async handleOpenActivity(activity, isInboundFlow = false, isNewlyCreatedActivity = false, fetchData = true) {
    await this.activityService.task.waitForCompletion();  // this is the flag for meeting note service call to finish
    if (this.activityService.prevSelectedActivity as SetBookingActivity && this.activityService.selectedActivity as AppointmentActivity
      && isNewlyCreatedActivity && this.activityService.isNewMeetingFlow && this.activityService.isLayOverPushedMeetingFromOtherActivities) return;
    this.presentationService.clearReferencesCache();
    this.uiService.showCancelDoneOnActivityDetails = false;
    this.activityDataService.activityDetailsLoaded = isInboundFlow;
    if (!activity.ID || isNewlyCreatedActivity || activity?.ID?.includes('offline')) {
      this.activityDataService.activityDetailsLoaded = true;
    }
    this.timeOffService.totMode = 'myRequests';
    this.uiService.showNewActivity = false;
    this.uiService.showFieldCoaching = false;
    let prevActivity = this.activityService.prevSelectedActivity;
    let foundActivity = this.activityService.getActivityByID(activity.ID);

    if (foundActivity) {
      this.activityService.selected = foundActivity;
    } else {
      this.activityService.selected = activity;
    }

    this.accountOfflineService.selectedAccounts = [];
    this.accountOfflineService.selected = null;
    if (activity.type !== ActivityType.Email && this.emailService.viewType != EmailViewType.CREATE_FROM_MEETING && this.emailService.viewType != EmailViewType.EMAIL_FROM_MEETING_PRESENTATION) {
      this.emailService.selectedActivity = null;
      this.emailService.setCurrentEmail(undefined);
    }

    if (activity.type === ActivityType.CaseIntake) {
      if (!activity.ID) {
        activity.ID = activity.offline_ID;
        activity._case_status_value = "Open";
      };
      if (!this.authService.impersonatedUser) {
        this.activityDataService.fetchCaseDetails(activity);
      }
    }
    this.uiService.showNewActivity = false;
    this.uiService.showRightPane = true;
    this.activityService.trackSelectedActivityId = '';
    switch (activity.type) {
      case ActivityType.Appointment:
        this.activityService.trackSelectedActivityId = activity.ID;
        this.navService.setChildNavRoot(PageName.ActivitiesDetailsPaneComponent, PageName.ActivitiesDetailsPaneComponent, PageName.GoAppActivitiesPageComponent, { isNewlyCreatedActivity: this.activityService.isNewActivityFlow, from: PageName.GoAppHomeComponent });
        break;
      case ActivityType.PhoneCall:
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        break;
      case ActivityType.Email:
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.emailService.viewType = EmailViewType.FROM_AGENDA;
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        this.emailService.selectedActivity = <EmailActivity>this.activityService.selectedActivity;
        this.navService.setChildNavRoot(PageName.EmailDetailsPageComponent, PageName.EmailDetailsPageComponent, PageName.GoAppActivitiesPageComponent, { from: PageName.GoAppActivitiesPageComponent });
        break;

      case ActivityType.CaseIntake:
        this.caseManagementService.accessedFrom = AccesingMode.AGENDA;
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        break;

      case ActivityType.Sample:
        this.sampleService.samplingDetailsViewMode = SamplingDetailsViewMode.VIEW_DETAILS;
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        this.navService.setChildNavRoot(PageName.NewSampleActivityComponent, PageName.NewSampleActivityComponent, PageName.GoAppActivitiesPageComponent, { from: PageName.GoAppHomeComponent });
        this.events.publish('selectedActivityChangedInBackround');
        break;
      case ActivityType.StoreCheck:
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        this.navService.setChildNavRoot(PageName.StoreCheckDetailsComponent, PageName.StoreCheckDetailsComponent, PageName.GoAppActivitiesPageComponent);
        break;

      case ActivityType.FollowUp:
        if (activity.scientificPlanId || (activity.planType && activity.planType == FOLLOW_UP_TYPE.SCIENTIFIC_PLAN)) {
          this.events.publish('updateCurrentFollowUpActivity');
          this.navService.setChildNavRoot(PageName.ScientificPlanFollowUpTask, PageName.ScientificPlanFollowUpTask, PageName.GoAppActivitiesPageComponent, { from: 'AgendaView', activity: this.activityService.selectedActivity });
        } else {
          this.navService.setChildNavRoot(PageName.FollowUpActionDetailComponent, PageName.FollowUpActionDetailComponent, PageName.GoAppActivitiesPageComponent, { from: 'AgendaView', activity: this.activityService.selectedActivity });
        }
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        break;

      case ActivityType.Order:
        this.trackingService.tracking('AgendaOrderInfo', TrackingEventNames.ORDERMANAGEMENT);
        this.navService.setChildNavRoot(PageName.OrderDetailsComponent, PageName.OrderDetailsComponent, PageName.GoAppActivitiesPageComponent, { from: 'AgendaView', activity: this.activityService.selectedActivity });
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        break;

      case ActivityType.TimeOff:
        this.loadTimeOffDetailsView(true);
        break;

      case ActivityType.TimeOffRequest:
        this.loadTimeOffDetailsView();
        break;

      case ActivityType.SurgeryOrder:
        this.navService.setChildNavRoot(PageName.SurgeryOrderDetailsComponent, PageName.SurgeryOrderDetailsComponent, PageName.GoAppActivitiesPageComponent, { from: PageName.GoAppHomeComponent, activity: this.activityService.selectedActivity });
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        break;
      case ActivityType.ProcedureTracker:
        this.navService.setChildNavRoot(PageName.ProcedureTrackerDetailsComponent, PageName.ProcedureTrackerDetailsComponent, PageName.GoAppActivitiesPageComponent, { from: 'AgendaView', activity: this.activityService.selectedActivity });
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        if (this.device.shouldBeMobileView) {
          this._showRightPane(isNewlyCreatedActivity);
        }
        break;
      case ActivityType.SetBooking:
        this.navService.setChildNavRoot(PageName.SetBookingActivityDetailsComponent, PageName.SetBookingActivityDetailsComponent, PageName.GoAppActivitiesPageComponent, { from: 'AgendaView', activity: this.activityService.selectedActivity, initFetchData: fetchData });
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        return;
      case ActivityType.MedicalInquiry:
        this.medicalInquiryService.setSelectedMI(activity as MedicalInquiry);
        this.navService.setChildNavRoot(PageName.MedicalInquiryDetailComponent, PageName.MedicalInquiryDetailComponent, PageName.GoAppActivitiesPageComponent, {
          from: 'AgendaView',
          activity: this.activityService.selectedActivity as MedicalInquiry,
        });
        this.uiService.activeView = 'ActivitiesPageRightPaneNav';
        this.uiService.showNewActivity = false;
        this.uiService.showRightPane = true;
        return;
      default:
        break;
    }

    if (isInboundFlow) {
      this.activityService.inboundMeetingFlagForActivitiesDetailsPane = true;
    }
    else {
      if (this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TOOL) && this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TEAM_VIEW)) {
        this.coachingReportService.setActivityCoaching(this.activityService.selectedActivity);
      }
      this._ngZone.runOutsideAngular(() => {
        if (!isNewlyCreatedActivity && prevActivity) {
          this.websocket.sendRemoteMeetingEnd(prevActivity, true);
          this.websocket.removeSubscription(prevActivity);
        }
        if (!this.authService.impersonatedUser) {
          if (!isNewlyCreatedActivity) {
            this.activityDataService.updateActivityDetails(activity);
          } else {
            this.activityDataService._appendMeetingDetailsToActivity(activity);
          }
        }
      });

      if (!this.device.isOffline && !activity.ID.includes('offline') && !this.authService.impersonatedUser) {
        if (this.activityService.selectedActivity instanceof AppointmentActivity) {
          (async () => this.websocket.subscribeToMeetingTopic(this.activityService.selectedActivity))();
        }
      }
    }

    if (this.activityService.selectedActivity && !isInboundFlow) {
      let activity = this.activityService.selectedActivity;
      switch (activity.type) {
        case ActivityType.Appointment:
          this.footerService.initButtons(FooterViews.Activities);
          break;
        case ActivityType.PhoneCall:
          this.navService.setChildNavRoot(PageName.PhoneCallDetailsComponent, PageName.PhoneCallDetailsComponent, PageName.GoAppActivitiesPageComponent);
          this.footerService.initButtons(FooterViews.PHONE_CALL);
          break;
        case ActivityType.Email:
          this.footerService.initButtons(FooterViews.EmailDetails);
          break;
        case ActivityType.Sample:
          this.footerService.initButtons(FooterViews.SampleDropDetails);
          break;
        case ActivityType.CaseIntake: {
          try {
            this.caseManagementService.accessedFrom = AccesingMode.AGENDA;
            this.caseManagementService.assignSelectedCase(<CaseActivity>activity);
          } catch (error) { }
          this.uiService.activeView = "ActivitiesPageRightPaneNav";
          this.navService.setChildNavRoot(PageName.CaseManagementDetailComponent, PageName.CaseManagementDetailComponent, PageName.GoAppActivitiesPageComponent, { from: PageName.GoAppHomeComponent });
          this.footerService.initButtons(FooterViews.CUSTOMER_INQUIRY);
          break;
        }
        case ActivityType.SurgeryOrder:
          this.footerService.initButtons(FooterViews.SURGERY_ORDER_ACTIVITY);
          break;
        default:
          this.footerService.initButtons('');
      }
    }

    this.activityService.selectedActivityAtHome = this.activityService.selectedActivity;

    this.events.publish('activities-pane:activitySelected');
    this.events.publish('scrollToTop');
    if (!isInboundFlow) {
      this.contactService.resetContactConnectionStatus();
    }

    if (this.activityService.selectedActivity instanceof AppointmentActivity) {
      this.events.publish('activities:appConfigFields');
    }

    if (this.activityService.selectedActivity instanceof PhoneActivity) {
      this.events.publish('phoneactivity:appConfigFields');
    }

    if (this.activityService.selectedActivity instanceof EmailActivity) {
      this.events.publish('emailActivity:loadChannels');
    }
  }

  private _showRightPane(isNewlyCreatedActivity: boolean) {
    this.uiService.showRightPane = true;
    setTimeout(() => {
      this._isAgendaRefreshed = isNewlyCreatedActivity;
    }, 0);
  }

  private loadTimeOffDetailsView(isTimeOff: boolean = false) {
    let tot: TimeOffActivity = <TimeOffActivity>this.activityService.selectedActivity;
    if (isTimeOff) {
      tot.name = tot.subject;
      tot.totIsAlldayEvent = tot.totIsAlldayEvent ?? tot.allDayEvent;
    }
    this.timeOffService.setSelectedTot(tot);
    this.uiService.activeView = "TimeOffDetail";
    this.timeOffService.totMode = 'myRequests';
    tot.totStartTime = tot.scheduledStart;
    tot.totEndTime = tot.scheduledEnd;
    this.navService.setChildNavRoot(PageName.TimeOffDetailsComponent, PageName.TimeOffDetailsComponent, PageName.GoAppActivitiesPageComponent, { from: PageName.GoAppHomeComponent });
    this.footerService.initButtons(FooterViews.TimeOffDetails);
  }

  ngOnDestroy() {
    this.profiler.endSession();
    this.activityService.trackSelectedActivityId = '';
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  async onNavDidChange() {
    this.uiService.showRightPane = this.navService.getActiveChildNavViewPageName() !== PageName.NothingSelectedView;
  }
}
